/**
 * Касания, клики, перетаскивания и прокрутка на записи экрана: без них на видео непонятно, куда нажимает пользователь.
 * DOM-запись (телефон) дорисовывает отметки в каждый кадр canvas; запись через getDisplayMedia (компьютер)
 * видит отметки на прозрачном слое поверх страницы (showInteractionOverlay).
 */

/** Сколько отметка касания гаснет после отпускания */
export const TAP_FADE_MS = 700
/** Хвост движения пальца или мыши с зажатой кнопкой */
export const TRAIL_MS = 800
export const WHEEL_FADE_MS = 600
/** Прокрутка колесом в одной точке сливается в одну отметку */
const WHEEL_MERGE_MS = 250
const WHEEL_MERGE_PX = 40
const MAX_TRAIL_POINTS = 120

interface Point {
  x: number
  y: number
  at: number
}

interface Stroke {
  points: Point[]
  upAt: number | null
  /** Правая кнопка мыши — другим цветом */
  secondary: boolean
}

interface Wheel {
  x: number
  y: number
  dx: number
  dy: number
  at: number
}

export type Mark =
  | { type: "press"; x: number; y: number; trail: Array<{ x: number; y: number }>; secondary: boolean; alpha: number; grow: number }
  | { type: "wheel"; x: number; y: number; dir: "up" | "down" | "left" | "right"; alpha: number }

export interface InteractionMarks {
  down: (id: string, x: number, y: number, secondary?: boolean) => void
  move: (id: string, x: number, y: number) => void
  up: (id: string) => void
  wheel: (x: number, y: number, dx: number, dy: number) => void
  /** Отметки на момент кадра; погасшие удаляются */
  marks: () => Mark[]
}

export function createInteractionMarks(now: () => number = Date.now): InteractionMarks {
  const strokes = new Map<string, Stroke>()
  const ended: Stroke[] = []
  const wheels: Wheel[] = []

  const finish = (id: string) => {
    const s = strokes.get(id)
    if (!s) return
    strokes.delete(id)
    s.upAt = now()
    ended.push(s)
  }

  return {
    down(id, x, y, secondary = false) {
      finish(id)
      strokes.set(id, { points: [{ x, y, at: now() }], upAt: null, secondary })
    },
    move(id, x, y) {
      const s = strokes.get(id)
      if (!s) return
      s.points.push({ x, y, at: now() })
      if (s.points.length > MAX_TRAIL_POINTS) s.points.splice(0, s.points.length - MAX_TRAIL_POINTS)
    },
    up: finish,
    wheel(x, y, dx, dy) {
      if (!dx && !dy) return
      const t = now()
      const last = wheels[wheels.length - 1]
      if (last && t - last.at < WHEEL_MERGE_MS && Math.hypot(last.x - x, last.y - y) < WHEEL_MERGE_PX) {
        last.dx += dx
        last.dy += dy
        last.x = x
        last.y = y
        last.at = t
        return
      }
      wheels.push({ x, y, dx, dy, at: t })
    },
    marks() {
      const t = now()
      const out: Mark[] = []
      const press = (s: Stroke, alpha: number, grow: number) => {
        const last = s.points[s.points.length - 1]
        // после отпускания хвост остаётся целиком и гаснет вместе с кругом: в кадр 4 раза в секунду иначе не попадёт
        const from = s.upAt ?? t
        const trail = s.points.filter((p) => from - p.at <= TRAIL_MS).map(({ x, y }) => ({ x, y }))
        out.push({ type: "press", x: last.x, y: last.y, trail, secondary: s.secondary, alpha, grow })
      }
      for (let i = ended.length - 1; i >= 0; i--) if (t - (ended[i].upAt ?? t) > TAP_FADE_MS) ended.splice(i, 1)
      for (const s of ended) {
        const k = 1 - (t - (s.upAt ?? t)) / TAP_FADE_MS
        press(s, k, 1 - k)
      }
      for (const s of strokes.values()) press(s, 1, 0)
      for (let i = wheels.length - 1; i >= 0; i--) if (t - wheels[i].at > WHEEL_FADE_MS) wheels.splice(i, 1)
      for (const w of wheels) {
        const dir = Math.abs(w.dy) >= Math.abs(w.dx) ? (w.dy > 0 ? "down" : "up") : w.dx > 0 ? "right" : "left"
        out.push({ type: "wheel", x: w.x, y: w.y, dir, alpha: 1 - (t - w.at) / WHEEL_FADE_MS })
      }
      return out
    },
  }
}

const PRESS_RGB = "244, 63, 94"
const SECONDARY_RGB = "168, 85, 247"
const WHEEL_RGB = "59, 130, 246"

/** Нарисовать отметки; координаты страницы переводятся в кадр: (x + dx) * scale */
export function drawInteractionMarks(g: CanvasRenderingContext2D, marks: Mark[], scale: number, offset: { x: number; y: number } = { x: 0, y: 0 }) {
  const px = (x: number) => (x + offset.x) * scale
  const py = (y: number) => (y + offset.y) * scale
  g.save()
  g.lineCap = "round"
  g.lineJoin = "round"
  for (const m of marks) {
    if (m.type === "press") {
      const rgb = m.secondary ? SECONDARY_RGB : PRESS_RGB
      if (m.trail.length > 1) {
        g.beginPath()
        g.moveTo(px(m.trail[0].x), py(m.trail[0].y))
        for (const p of m.trail.slice(1)) g.lineTo(px(p.x), py(p.y))
        g.strokeStyle = `rgba(${rgb}, ${0.55 * m.alpha})`
        g.lineWidth = 6 * scale
        g.stroke()
      }
      g.beginPath()
      g.arc(px(m.x), py(m.y), (18 + 10 * m.grow) * scale, 0, Math.PI * 2)
      g.fillStyle = `rgba(${rgb}, ${0.35 * m.alpha})`
      g.strokeStyle = `rgba(${rgb}, ${0.9 * m.alpha})`
      g.lineWidth = 3 * scale
      g.fill()
      g.stroke()
    } else {
      const x = px(m.x)
      const y = py(m.y)
      const r = 20 * scale
      g.beginPath()
      g.arc(x, y, r, 0, Math.PI * 2)
      g.fillStyle = `rgba(${WHEEL_RGB}, ${0.25 * m.alpha})`
      g.strokeStyle = `rgba(${WHEEL_RGB}, ${0.9 * m.alpha})`
      g.lineWidth = 2 * scale
      g.fill()
      g.stroke()
      // две «галочки» по направлению прокрутки
      const angle = { down: Math.PI / 2, up: -Math.PI / 2, right: 0, left: Math.PI }[m.dir]
      g.save()
      g.translate(x, y)
      g.rotate(angle)
      g.beginPath()
      for (const shift of [-5, 4]) {
        g.moveTo((shift - 4) * scale, -7 * scale)
        g.lineTo((shift + 4) * scale, 0)
        g.lineTo((shift - 4) * scale, 7 * scale)
      }
      g.strokeStyle = `rgba(${WHEEL_RGB}, ${m.alpha})`
      g.lineWidth = 3 * scale
      g.stroke()
      g.restore()
    }
  }
  g.restore()
}

export interface InteractionTracker {
  marks: () => Mark[]
  stop: () => void
}

/** Слушать касания, мышь и колесо на всей странице, пока идёт запись */
export function trackInteractions(): InteractionTracker {
  const model = createInteractionMarks()
  const opts = { capture: true, passive: true } as const
  const onPointerDown = (e: PointerEvent) => e.pointerType !== "touch" && model.down(`p${e.pointerId}`, e.clientX, e.clientY, e.button === 2)
  const onPointerMove = (e: PointerEvent) => e.pointerType !== "touch" && e.buttons && model.move(`p${e.pointerId}`, e.clientX, e.clientY)
  const onPointerUp = (e: PointerEvent) => e.pointerType !== "touch" && model.up(`p${e.pointerId}`)
  // касания — через touch-события: pointer-события пальца обрываются (pointercancel), как только начинается прокрутка
  const onTouchStart = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) model.down(`t${t.identifier}`, t.clientX, t.clientY)
  }
  const onTouchMove = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) model.move(`t${t.identifier}`, t.clientX, t.clientY)
  }
  const onTouchEnd = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) model.up(`t${t.identifier}`)
  }
  const onWheel = (e: WheelEvent) => model.wheel(e.clientX, e.clientY, e.deltaX, e.deltaY)
  const listeners: Array<[string, EventListener]> = [
    ["pointerdown", onPointerDown as EventListener],
    ["pointermove", onPointerMove as EventListener],
    ["pointerup", onPointerUp as EventListener],
    ["pointercancel", onPointerUp as EventListener],
    ["touchstart", onTouchStart as EventListener],
    ["touchmove", onTouchMove as EventListener],
    ["touchend", onTouchEnd as EventListener],
    ["touchcancel", onTouchEnd as EventListener],
    ["wheel", onWheel as EventListener],
  ]
  for (const [type, fn] of listeners) window.addEventListener(type, fn, opts)
  return {
    marks: model.marks,
    stop: () => {
      for (const [type, fn] of listeners) window.removeEventListener(type, fn, opts)
    },
  }
}

/**
 * Прозрачный слой поверх страницы с отметками — для записи через getDisplayMedia, которая снимает страницу как есть.
 * Возвращает функцию, которая убирает слой.
 */
export function showInteractionOverlay(): () => void {
  const tracker = trackInteractions()
  const canvas = document.createElement("canvas")
  canvas.setAttribute("aria-hidden", "true")
  canvas.setAttribute("data-interaction-overlay", "")
  Object.assign(canvas.style, { position: "fixed", inset: "0", width: "100vw", height: "100vh", pointerEvents: "none", zIndex: "2147483647" })
  document.body.appendChild(canvas)
  const g = canvas.getContext("2d")
  let ratio = 1
  const resize = () => {
    ratio = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = Math.round(window.innerWidth * ratio)
    canvas.height = Math.round(window.innerHeight * ratio)
  }
  resize()
  window.addEventListener("resize", resize)
  let dirty = false
  let raf = 0
  const tick = () => {
    raf = requestAnimationFrame(tick)
    if (!g) return
    const marks = tracker.marks()
    if (!marks.length && !dirty) return
    g.clearRect(0, 0, canvas.width, canvas.height)
    drawInteractionMarks(g, marks, ratio)
    dirty = marks.length > 0
  }
  raf = requestAnimationFrame(tick)
  return () => {
    cancelAnimationFrame(raf)
    window.removeEventListener("resize", resize)
    tracker.stop()
    canvas.remove()
  }
}
